// Carry Roll Selectors — pure reads. No engine calls inside (except via store recompute path).

import { useEffect, useRef } from 'react';
import { useStore } from './index';
import { computeResult } from './resultSlice';
import { validate } from '@carry-roll/shared-logic';
import { confidence } from '@carry-roll/shared-logic';
import {
  convertDistance,
  mToFt,
  type DistanceUnit,
  type HeightUnit,
} from '@carry-roll/shared-logic';
import type {
  ConfidenceLevel,
  FlightResult,
  SimulationRequest,
  ValidationWarning,
} from '@carry-roll/shared-types';
import { InputMode } from '@carry-roll/shared-types';

/**
 * Wires the memoized recompute: whenever request or fidelity changes, recompute runs
 * once. This is the single path that invokes the engine.
 */
export function useRecompute(): void {
  const request = useStore((s) => s.request);
  const fidelity = useStore((s) => s.fidelity);
  const recompute = useStore((s) => s.recompute);
  const last = useRef<{ request: SimulationRequest; fidelity: typeof fidelity } | null>(null);
  useEffect(() => {
    if (last.current && last.current.request === request && last.current.fidelity === fidelity) {
      return;
    }
    last.current = { request, fidelity };
    recompute({ ...request, fidelity });
  }, [request, fidelity, recompute]);
}

export function useResult(): FlightResult {
  return useStore((s) => s.result);
}

export interface SplitDisplay {
  carryDisplay: string;
  rollDisplay: string;
  totalDisplay: string;
  measuredDisplay: string;
  apexDisplay: string;
  rollPercent: number;
  carryPercent: number;
}

export function useSplitDisplay(): SplitDisplay {
  const result = useStore((s) => s.result);
  const distanceUnit = useStore((s) => s.distanceUnit);
  const heightUnit = useStore((s) => s.heightUnit);
  return buildSplit(result, distanceUnit, heightUnit);
}

function buildSplit(
  result: FlightResult,
  distanceUnit: DistanceUnit,
  heightUnit: HeightUnit,
): SplitDisplay {
  const conv = (m: number) => convertDistance(m, distanceUnit);
  return {
    carryDisplay: `${Math.round(conv(result.carryM))} ${distanceUnit}`,
    rollDisplay: `${Math.round(conv(result.rollM))} ${distanceUnit}`,
    totalDisplay: `${Math.round(conv(result.totalM))} ${distanceUnit}`,
    measuredDisplay: `${Math.round(conv(result.measuredM))} ${distanceUnit}`,
    apexDisplay: `${Math.round(heightUnit === 'ft' ? mToFt(result.apexM) : result.apexM)} ${heightUnit}`,
    rollPercent: result.rollPercent,
    carryPercent: 1 - result.rollPercent,
  };
}

export interface SmashDisplay {
  smash: number | null;
  valid: boolean;
}

export function useSmashFactor(): SmashDisplay {
  const ballSpeed = useStore((s) => s.request.launch.ballSpeed);
  const clubSpeed = useStore((s) => s.request.launch.clubSpeed);
  if (clubSpeed == null || clubSpeed <= 0 || ballSpeed <= 0) return { smash: null, valid: true };
  const smash = ballSpeed / clubSpeed;
  return { smash, valid: smash >= 1.0 && smash <= 1.55 };
}

export function useConfidence(): ConfidenceLevel {
  const request = useStore((s) => s.request);
  return confidence(request);
}

export function useValidation(): ValidationWarning[] {
  const request = useStore((s) => s.request);
  return validate(request);
}

export function useRequest(): SimulationRequest {
  return useStore((s) => s.request);
}

export function useInputMode(): InputMode {
  return useStore((s) => s.inputMode);
}

// Re-export computeResult so the memoized path is the only engine entry.
export { computeResult };
